import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from "react-router-dom";
import {Avatar as PrimeAvatar} from "primereact/avatar";
import {Button} from "primereact/button";
import {useSelector} from "react-redux";
import {useTranslate} from "../../system/useTranslate";
import {usePost} from "../../models/post/use-post";
import {public_url} from "../../system/utils";
import api from "../../system/api";
import {APP_URL, SupportRequestDialog} from "./components/help-sections";
import {
    ArticleView,
    buildSearchItems,
    CategoryView,
    HomeView,
    ListingView,
    PathView,
    SearchView,
    SupportStrip,
    VideoView,
} from "./components/help-views";
import {applyRemoteHelpContent, HELP_CATEGORIES} from "./help-content";

const NAV = [
    {view: "home", label: "خانه", match: ["home"]},
    {view: "articles", label: "مقالات", match: ["articles", "article"]},
    {view: "videos", label: "ویدئوها", match: ["videos", "video"]},
    {view: "path", label: "شروع سریع", match: ["path"]},
];

function Index() {
    const [params, setParams] = useSearchParams();
    const [posts, setPosts] = useState([]);
    const [requestVisible, setRequestVisible] = useState(false);
    const [contentReady, setContentReady] = useState(false);
    const scroller = useRef(null);
    const navigate = useNavigate();
    const post = usePost();
    const view = params.get("view") || "home";
    const current = Object.fromEntries(params.entries());

    // Published content comes from the admin panel; the static content is the fallback.
    useEffect(() => {
        (async () => {
            try {
                const {data} = await api.get("/help-center/content");
                applyRemoteHelpContent(data?.data);
            } catch (error) {
                // keep the static content
            }
            setContentReady(true);
        })();
    }, []);
    useEffect(() => {
        scroller.current?.scrollTo?.(0, 0);
    }, [params]);

    // contentReady: the static arrays are replaced in place once the CMS content is loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const searchItemsList = useMemo(() => buildSearchItems(posts), [posts, contentReady]);
    const go = (next) => {
        const clean = Object.fromEntries(Object.entries(next).filter(([, value]) => value && value !== "all"));
        setParams(clean);
    };
    const openItem = (item) => {
        if (item.kind === "post") navigate(`/help/${item.documentId}`);
        else go({view: item.kind === "video" ? "video" : "article", id: item.id});
    };
    // Stored in the admin panel (support app -> "درخواست‌های پشتیبانی").
    const submitRequest = async (values) => {
        const category = HELP_CATEGORIES.find((item) => item.id === values.category);
        await api.post("/support-requests", {
            fullName: values.name,
            mobile: values.mobile,
            subject: values.subject,
            description: values.description,
            priority: values.priority,
            categoryId: category?.id,
            categoryTitle: category?.title,
        });
    };
    const onRequest = () => setRequestVisible(true);
    const shared = {go, openItem, onRequest, searchItemsList, params: current};

    let content;
    if (view === "articles" || view === "videos") content = <ListingView kind={view} {...shared}/>;
    else if (view === "search") content = <SearchView {...shared}/>;
    else if (view === "category") content = <CategoryView id={current.cat} {...shared}/>;
    else if (view === "path") content = <PathView {...shared}/>;
    else if (view === "article") content = <ArticleView id={current.id} {...shared}/>;
    else if (view === "video") content = <VideoView id={current.id} {...shared}/>;
    else content = (<><HomeView {...shared}/><SupportStrip onRequest={onRequest}/></>);

    return (
        <div className="h-full flex flex-column overflow-hidden">
            <Toolbar view={view} go={go} onSendRequest={onRequest}/>
            <div className="flex flex-column flex-1 overflow-auto" ref={scroller}>
                <div className="flex-1">{contentReady && content}</div>
                <Footer/>
            </div>
            <SupportRequestDialog visible={requestVisible} onHide={() => setRequestVisible(false)} onSubmit={submitRequest} categories={HELP_CATEGORIES.map((c) => ({documentId: c.id, title: c.title}))}/>
        </div>
    );
}

function Toolbar({view, go, onSendRequest}) {
    const {config} = useSelector((state) => state.app);
    const translate = useTranslate();
    return (
        <div className="w-full surface-100 border-bottom-1 border-300 p-2 flex align-items-center justify-content-between">
            <div className="flex align-items-center gap-1">
                <PrimeAvatar size="xlarge" className="h-auto cursor-pointer" image={public_url(config["sidebar-logo"])} onClick={() => go({view: "home"})}/>
                <span className="font-bold border-left-2 border-300 pl-3">{translate("help-center")}</span>
                <div className="flex align-items-center gap-1 mr-3">
                    {NAV.map((item) => (
                        <Button
                            key={item.view}
                            className={`text-xs p-2 px-3 ${item.match.includes(view) ? "" : "p-button-text p-button-secondary"}`}
                            label={item.label}
                            onClick={() => go({view: item.view})}
                        />
                    ))}
                </div>
            </div>
            <div className="flex align-items-center gap-2">
                <Button className="text-xs p-2 p-button-text p-button-secondary" label={translate("send-request")} onClick={onSendRequest}/>
                <Button className="text-xs p-2" label={translate("sign-to-app")} onClick={() => window.location.assign(APP_URL)}/>
            </div>
        </div>
    )
}

function Footer() {
    const translate = useTranslate();
    return(
        <div className="p-3 pl-4 pr-4 flex align-items-center justify-content-between text-400">
            <div className="flex align-items-center gap-2">
                <Button className="p-button-secondary border-none shadow-none text-xs p-button-text p-2 pr-3 pl-3" label={translate("privacy")}/>
                <Button className="p-button-secondary border-none shadow-none text-xs p-button-text p-2 pr-3 pl-3" label={translate("rules")}/>
                <Button className="p-button-secondary border-none shadow-none text-xs p-button-text p-2 pr-3 pl-3" label={translate("system-status")}/>
            </div>
            <div>
                <span className="text-sm">© ۱۴۰۳ زوفینو. تمامی حقوق محفوظ است.</span>
            </div>
        </div>
    )
}
export default Index;